import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Compass, Heart, Sparkles } from 'lucide-react';

interface HeroProps {
  onExplore: () => void;
  onStart: () => void;
}

const Hero: React.FC<HeroProps> = ({ onExplore, onStart }) => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-pink-50 via-fuchsia-50 to-purple-100">
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 text-center">

        <div className="mx-auto mb-8 flex max-w-xl items-center justify-center rounded-3xl border border-fuchsia-100 bg-white/90 p-5 shadow-xl">
          <img
            src="/brand/kinscircle-logo-banner.png"
            alt="KinsCircle - Bringing Families Together"
            className="w-full max-w-md rounded-2xl object-contain"
          />
        </div>

        <div className="inline-flex items-center gap-2 rounded-full bg-white/75 px-4 py-1.5 border border-fuchsia-100 shadow-sm mb-6">
          <Sparkles className="w-3.5 h-3.5 text-fuchsia-600" />
          <span className="text-xs font-medium text-fuchsia-700 tracking-wide">
            A Living Family Network · for modern families
          </span>
        </div>

        <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight mb-6">
          Your Family&apos;s Story.
          <span className="block bg-gradient-to-r from-fuchsia-600 via-pink-500 to-rose-400 bg-clip-text text-transparent">
            Preserved Forever.
          </span>
        </h1>

        <p className="text-lg md:text-xl text-slate-600 mb-9 leading-relaxed max-w-2xl mx-auto">
          KinsCircle is a private family operating system that holds your whole family —
          partners, in-laws, blended households, and every generation — in one shared space.
          Preserve memories, stay close, and keep private family conversations in one trusted space.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={onStart}
            size="lg"
            className="bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-700 hover:to-purple-800 text-white rounded-full px-7 h-12 shadow-lg shadow-fuchsia-500/25"
          >
            <Heart className="mr-2 w-4 h-4 fill-white" />
            Start Your Family Network
          </Button>

          <Button
            onClick={onExplore}
            size="lg"
            variant="outline"
            className="rounded-full px-7 h-12 border-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 bg-white/80 backdrop-blur"
          >
            <Compass className="mr-2 w-4 h-4" />
            Explore Your Family
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
